/**
 * Typed wrappers around the Amplify v6 auth API.
 *
 * Keeping the SDK surface in one file lets us mock the whole module in
 * tests without touching every call site and gives us a single place to
 * shape Amplify's return values into the domain types the UI uses.
 *
 * The Cognito user pool runs a PreSignUp Lambda that auto-confirms
 * every new account, so there is NO email-confirmation step in this
 * codebase. After `signUp` Amplify returns either `DONE` (rare) or
 * `COMPLETE_AUTO_SIGN_IN`, and we call `autoSignIn` to finalise the
 * session. Callers always treat sign-up as "you're in".
 *
 * E2E TESTS: when `VITE_E2E_AUTH_BYPASS=1` is set on a local build the
 * functions short-circuit to an in-memory fake (see `lib/e2e-bypass`).
 * This lets Playwright drive signed-in flows without a real Cognito
 * round trip; the bypass is hard-gated to local builds.
 */
import {
  autoSignIn as amplifyAutoSignIn,
  fetchAuthSession as amplifyFetchAuthSession,
  getCurrentUser as amplifyGetCurrentUser,
  signIn as amplifySignIn,
  signInWithRedirect as amplifySignInWithRedirect,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import { fakeAuth, isE2EAuthBypass } from '@/lib/e2e-bypass';

import type { AuthUser, SocialProvider } from '../types';

function dispatchFakeAuthEvent(event: 'signedIn' | 'signedOut'): void {
  Hub.dispatch('auth', { event });
}

export interface SignUpInput {
  email: string;
  password: string;
  /** Full name · stored as Cognito `name` attribute and mirrored to
   *  `users.display_name` by the backend on the first authed request. */
  name?: string;
}

export interface SignInInput {
  email: string;
  password: string;
}

export async function signUp({ email, password, name }: SignUpInput): Promise<void> {
  if (isE2EAuthBypass) {
    // Mirror real Cognito: the typed full name becomes the user's
    // display name. Keeps bypass-driven flows (E2E, design-parity
    // diffs) faithful to identity-personalised copy.
    fakeAuth.signIn({ email, displayName: name && name.trim() ? name.trim() : null });
    dispatchFakeAuthEvent('signedIn');
    return;
  }
  const userAttributes: Record<string, string> = { email };
  if (name && name.trim()) {
    userAttributes.name = name.trim();
  }
  const result = await amplifySignUp({
    username: email,
    password,
    options: {
      userAttributes,
      autoSignIn: true,
    },
  });
  // PreSignUp Lambda auto-confirms every new user, so Amplify either
  // hands us a finished session (`DONE`) or asks us to finalise the
  // auto-sign-in. We never expect `CONFIRM_SIGN_UP` from this pool.
  if (result.nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
    await amplifyAutoSignIn();
  }
}

/**
 * Ask the Flask backend whether an email is already registered.
 *
 * Returns true if a row with that email (case-insensitive) exists in
 * `users`. The SPA uses this after the email step in the unified entry
 * form to branch between LOGIN (password only) and REGISTER (full name
 * + password). Falls back to `false` on network errors so the user
 * still gets to register — Cognito will reject a duplicate signup if
 * that's wrong and we surface the error inline.
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  if (isE2EAuthBypass) return false;
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  try {
    const res = await fetch(`${base}/api/v1/auth/email-exists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { exists?: boolean };
    return data.exists === true;
  } catch {
    return false;
  }
}

export async function signIn({ email, password }: SignInInput): Promise<void> {
  if (isE2EAuthBypass) {
    fakeAuth.signIn();
    dispatchFakeAuthEvent('signedIn');
    return;
  }
  await amplifySignIn({
    username: email,
    password,
    options: { authFlowType: 'USER_SRP_AUTH' },
  });
}

export async function signOut(): Promise<void> {
  if (isE2EAuthBypass) {
    fakeAuth.signOut();
    dispatchFakeAuthEvent('signedOut');
    return;
  }
  await amplifySignOut();
}

export async function signInWithSocial(provider: SocialProvider): Promise<void> {
  if (isE2EAuthBypass) {
    fakeAuth.signIn();
    dispatchFakeAuthEvent('signedIn');
    return;
  }
  await amplifySignInWithRedirect({ provider });
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  if (isE2EAuthBypass) {
    return fakeAuth.getUser();
  }
  try {
    const user = await amplifyGetCurrentUser();
    const session = await amplifyFetchAuthSession();
    const claims = session.tokens?.idToken?.payload ?? {};
    const email =
      typeof claims['email'] === 'string' ? claims['email'] : (user.signInDetails?.loginId ?? '');
    const emailVerified = claims['email_verified'] === true;
    const name = typeof claims['name'] === 'string' ? claims['name'] : null;
    return {
      userId: user.userId,
      email,
      emailVerified,
      displayName: name,
    };
  } catch {
    return null;
  }
}
